import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Box,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import { HelpCircle, Info } from "lucide-react";
import ProductSection from "../ProductSection";
import { useFetchBrandsQuery } from "../../../../../services/baseQuery";

const PricingDetails = ({ productData, updateProductData, errors }) => {
  const [calculatedSellingPrice, setCalculatedSellingPrice] = useState(0);
  const [sellConditionType, setSellConditionType] = useState(
    productData.sellCondition?.type || "CUSTOM_NONE",
  );

  // Fetch brand details to get order validation settings
  const { data: brandsData, isLoading: isBrandsLoading } = useFetchBrandsQuery({
    status: "ACTIVE",
  });

  // Find the selected brand
  const selectedBrand = brandsData?.data?.brands?.find(
    brand => brand._id === productData.brand,
  );

  useEffect(() => {
    // Initialize sell condition from product data
    if (productData.sellCondition) {
      setSellConditionType(productData.sellCondition.type);
    }
  }, [productData.sellCondition]);

  useEffect(() => {
    if (productData.mrp && productData.discount) {
      const discountAmount = (productData.mrp * productData.discount) / 100;
      const newSellingPrice = productData.mrp - discountAmount;
      setCalculatedSellingPrice(newSellingPrice.toFixed(2));
    }
  }, [productData.mrp, productData.discount]);

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === "mrp" || name === "discount") {
      let updatedData = { [name]: value };

      // Auto-calculate selling price
      if (name === "mrp" && value && productData.discount) {
        const discountAmount = (value * productData.discount) / 100;
        updatedData.sellingPrice = (value - discountAmount).toFixed(2);
      } else if (name === "discount" && value && productData.mrp) {
        const discountAmount = (productData.mrp * value) / 100;
        updatedData.sellingPrice = (productData.mrp - discountAmount).toFixed(
          2,
        );
      }

      updateProductData(updatedData);
    } else {
      updateProductData({ [name]: value });
    }
  };

  const handleSellConditionTypeChange = event => {
    const type = event.target.value;
    setSellConditionType(type);

    // Update product data with new sell condition
    if (type === "CUSTOM_NONE") {
      updateProductData({
        sellCondition: {
          type: "CUSTOM_NONE",
        },
      });
    } else if (type === "FOLLOW_BRAND") {
      // When following brand settings, we don't need to store additional data
      updateProductData({
        sellCondition: {
          type: "FOLLOW_BRAND",
        },
      });
    } else if (type === "CUSTOM_MOQ_MOV") {
      // Initialize with default values
      updateProductData({
        sellCondition: {
          type: "CUSTOM_MOQ_MOV",
          minOrderQty: "",
          minOrderValue: "",
          validationCondition: "ANY_ONE",
        },
      });
    }
  };

  const handleSellConditionChange = (field, value) => {
    updateProductData({
      sellCondition: {
        ...productData.sellCondition,
        [field]: value,
      },
    });
  };

  // Helper function to render brand's sell condition details
  const renderBrandSellCondition = () => {
    if (!selectedBrand || isBrandsLoading) {
      return (
        <Box className="flex items-center justify-center p-4">
          <CircularProgress size={20} />
          <Typography variant="body2" className="ml-2">
            Loading brand settings...
          </Typography>
        </Box>
      );
    }

    const orderValidation = selectedBrand.orderValidation || {
      validationType: "NONE",
    };

    if (orderValidation.validationType === "NONE") {
      return (
        <Box className="p-4 bg-gray-50 rounded-lg">
          <Typography variant="body2" className="text-gray-600">
            This brand has no order validation requirements.
          </Typography>
        </Box>
      );
    }

    return (
      <Box className="p-4 bg-gray-50 rounded-lg">
        <Typography variant="subtitle2" className="font-medium mb-2">
          Brand Order Validation Settings
        </Typography>

        {orderValidation.minOrderQty && (
          <Typography variant="body2" className="text-gray-600">
            Minimum Order Quantity:{" "}
            <span className="font-medium">{orderValidation.minOrderQty}</span>
          </Typography>
        )}

        {orderValidation.minOrderValue && (
          <Typography variant="body2" className="text-gray-600">
            Minimum Order Value:{" "}
            <span className="font-medium">
              ₹{orderValidation.minOrderValue}
            </span>
          </Typography>
        )}

        {orderValidation.validationCondition && (
          <Typography variant="body2" className="text-gray-600 mt-1">
            Validation Condition:{" "}
            <span className="font-medium">
              {orderValidation.validationCondition === "ANY_ONE"
                ? "Any One"
                : "Both"}
            </span>
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <div className="space-y-6">
      <ProductSection data="Pricing Information" />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="MRP (₹)"
            name="mrp"
            type="number"
            value={productData.mrp}
            onChange={handleChange}
            fullWidth
            size="small"
            error={!!errors.mrp}
            helperText={errors.mrp}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Discount (%)"
            name="discount"
            type="number"
            value={productData.discount}
            onChange={handleChange}
            fullWidth
            size="small"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Selling Price (₹)"
            name="sellingPrice"
            type="number"
            value={productData.sellingPrice}
            onChange={handleChange}
            fullWidth
            size="small"
            error={!!errors.sellingPrice}
            helperText={errors.sellingPrice}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Purchase Price (₹)"
            name="purchasePrice"
            type="number"
            value={productData.purchasePrice}
            onChange={handleChange}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
              sx: {
                borderRadius: "0.5rem",
                fontFamily: "Outfit",
              },
            }}
            InputLabelProps={{
              sx: {
                fontFamily: "Outfit",
              },
            }}
          />
        </Grid>
      </Grid>

      {/* Calculated Selling Price Info */}
      {productData.mrp > 0 && productData.discount > 0 && (
        <Box className="p-3 bg-blue-50 rounded-lg flex items-start">
          <Info size={20} className="text-blue-500 mr-2 mt-0.5" />
          <div>
            <Typography variant="body2" className="text-blue-700">
              Calculated Selling Price: ₹{calculatedSellingPrice}
            </Typography>
            <Typography variant="caption" className="text-blue-600">
              Based on MRP ₹{productData.mrp} with {productData.discount}%
              discount
            </Typography>
          </div>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Sell Condition Section */}
      <ProductSection data="Sell Condition" />

      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <Typography variant="subtitle2" className="mb-3 font-medium">
          Order Validation Requirements
        </Typography>

        <RadioGroup
          value={sellConditionType}
          onChange={handleSellConditionTypeChange}
          className="mb-4">
          <FormControlLabel
            value="CUSTOM_NONE"
            control={
              <Radio
                size="small"
                sx={{ color: "#5652B0", "&.Mui-checked": { color: "#5652B0" } }}
              />
            }
            label="No Requirements"
          />
          <FormControlLabel
            value="FOLLOW_BRAND"
            control={
              <Radio
                size="small"
                sx={{ color: "#5652B0", "&.Mui-checked": { color: "#5652B0" } }}
              />
            }
            label="Follow Brand Settings"
          />
          <FormControlLabel
            value="CUSTOM_MOQ_MOV"
            control={
              <Radio
                size="small"
                sx={{ color: "#5652B0", "&.Mui-checked": { color: "#5652B0" } }}
              />
            }
            label="Custom Requirements"
          />
        </RadioGroup>

        {sellConditionType === "FOLLOW_BRAND" && renderBrandSellCondition()}

        {sellConditionType === "CUSTOM_MOQ_MOV" && (
          <Grid container spacing={3} className="mt-1">
            <Grid item xs={12} md={6}>
              <TextField
                label="Minimum Order Quantity"
                type="number"
                value={productData.sellCondition?.minOrderQty || ""}
                onChange={e =>
                  handleSellConditionChange("minOrderQty", e.target.value)
                }
                fullWidth
                size="small"
                InputProps={{
                  sx: {
                    borderRadius: "0.5rem",
                    fontFamily: "Outfit",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    fontFamily: "Outfit",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Minimum Order Value (₹)"
                type="number"
                value={productData.sellCondition?.minOrderValue || ""}
                onChange={e =>
                  handleSellConditionChange("minOrderValue", e.target.value)
                }
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  sx: {
                    borderRadius: "0.5rem",
                    fontFamily: "Outfit",
                  },
                }}
                InputLabelProps={{
                  sx: {
                    fontFamily: "Outfit",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel
                  id="validation-condition-label"
                  sx={{ fontFamily: "Outfit" }}>
                  Validation Condition
                </InputLabel>
                <Select
                  labelId="validation-condition-label"
                  value={
                    productData.sellCondition?.validationCondition || "ANY_ONE"
                  }
                  onChange={e =>
                    handleSellConditionChange(
                      "validationCondition",
                      e.target.value,
                    )
                  }
                  label="Validation Condition"
                  sx={{
                    borderRadius: "0.5rem",
                    fontFamily: "Outfit",
                  }}>
                  <MenuItem value="ANY_ONE">
                    Any One (Quantity OR Value)
                  </MenuItem>
                  <MenuItem value="BOTH">Both (Quantity AND Value)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        <Box className="mt-4 p-3 bg-gray-50 rounded-lg flex items-start">
          <HelpCircle size={18} className="text-gray-500 mr-2 mt-0.5" />
          <Typography variant="caption" className="text-gray-600">
            Order validation requirements determine the minimum quantity or
            value a customer must order. You can set custom requirements for
            this product or follow the brand's settings.
          </Typography>
        </Box>
      </div>
    </div>
  );
};

export default PricingDetails;
